// Admin idle-logout + session-lifetime guard.
//
// Two timers, both client-side, backing the server-side enforcement in
// the Convex backend (lib/admin.ts rejects requests once a session goes
// 30 min without a heartbeat, and the JWT itself expires after 8h):
//
//   • Idle:    30 min with no user interaction → sign out.
//   • Absolute: when the stored token's expires_at passes → sign out.
//
// While the admin is active we send a throttled heartbeat (≤ 1/min) so
// the server's sliding idle window stays fresh; if a heartbeat comes
// back 401 (session already dead server-side) we sign out immediately.
//
// Keep ADMIN_IDLE_MS in sync with the backend's ADMIN_IDLE_MS (lib/admin.ts).

import { useEffect, useRef } from 'react';
import { adminSession } from './api';
import { ENVIRONMENTS, type AdminEnv } from './environments';

export const ADMIN_IDLE_MS = 30 * 60 * 1000; // 30 min
const HEARTBEAT_THROTTLE_MS = 60 * 1000; // ≤ 1 heartbeat/min
const CHECK_INTERVAL_MS = 30 * 1000; // re-evaluate timers every 30s

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

/**
 * Heartbeat both environments.
 *
 * Sessions idle out after 30 min (lib/admin.ts ADMIN_IDLE_MS), refreshed
 * by this heartbeat. With two live sessions, heartbeating only the
 * active one means 31 minutes in dev idles out prod — and toggling to
 * prod drops you at a login screen, which is the exact friction the
 * unified console exists to remove.
 *
 * This does loosen the idle timeout: it now means "30 min since you
 * touched the console" rather than "30 min since you touched prod". That
 * is the right reading — the control bounds an unattended browser, and
 * the JWT's 8h absolute TTL is unaffected.
 *
 * A failure against one environment drops only that session. A dev
 * heartbeat failure must never sign you out of prod.
 */
export async function heartbeatAll(): Promise<void> {
  await Promise.all(
    (Object.keys(ENVIRONMENTS) as AdminEnv[]).map(async (env) => {
      const token = adminSession.getToken(env);
      if (!token) return;
      try {
        const res = await fetch(`${ENVIRONMENTS[env].apiBase}/admin/auth/heartbeat`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          adminSession.clear(env);
        }
      } catch {
        /* transient network failure — leave the session, try next tick */
      }
    }),
  );
}

/**
 * useIdleLogout — wire into the authenticated shell (DashboardPage).
 * `onLogout` should redirect to sign-in, and — for 'idle'/'expired' only
 * — revoke + clear every environment's session first. It may be called
 * from a timer ('idle'/'expired': genuine user-intent-equivalent logout,
 * clear everything) or a failed heartbeat ('session-lost': only the
 * active environment's session died on its own; another environment may
 * still hold a valid session and must not be touched).
 */
export function useIdleLogout(onLogout: (reason: 'idle' | 'expired' | 'session-lost') => void) {
  const lastActivity = useRef<number>(Date.now());
  const lastHeartbeat = useRef<number>(0);
  // Hold the latest onLogout without re-subscribing listeners each render.
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  useEffect(() => {
    let stopped = false;

    const markActivity = () => {
      lastActivity.current = Date.now();
      // Throttled heartbeat so the server idle window tracks real use.
      // Fans out to both environments (heartbeatAll) so idling in one
      // never silently expires the other's session server-side.
      if (Date.now() - lastHeartbeat.current >= HEARTBEAT_THROTTLE_MS) {
        lastHeartbeat.current = Date.now();
        void heartbeatAll().then(() => {
          // heartbeatAll clears a namespaced session on 401. If that was
          // the ACTIVE environment's session, the server already
          // considers it dead — redirect immediately rather than waiting
          // for the next interval tick. A dev-only 401 while prod is
          // active never reaches this: only the environment that 401'd
          // gets cleared, and isValid() here checks the active one.
          //
          // This is 'session-lost', not 'idle': the user did not go
          // away (activity just fired, which is why we're heartbeating
          // at all) — the active env's session died server-side on its
          // own. Reporting it as 'idle' would make the caller clearAll(),
          // wiping a still-valid session in the other environment.
          if (!stopped && !adminSession.isValid()) {
            onLogoutRef.current('session-lost');
          }
        });
      }
    };

    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, markActivity, { passive: true });
    }

    const interval = window.setInterval(() => {
      if (stopped) return;
      // Absolute lifetime: stored token expired.
      if (!adminSession.isValid()) {
        onLogoutRef.current('expired');
        return;
      }
      // Idle: no interaction within the window.
      if (Date.now() - lastActivity.current > ADMIN_IDLE_MS) {
        onLogoutRef.current('idle');
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      stopped = true;
      window.clearInterval(interval);
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, markActivity);
      }
    };
  }, []);
}
